import CheckIcon from '@mui/icons-material/Check';
import DirectionsBoatFilledIcon from '@mui/icons-material/DirectionsBoatFilled';
import { FormControlLabel, ListItemButton, ListItemIcon, ListItemText, Stack, Switch, Typography } from '@mui/material';
import { AbsDropDown } from 'customer_portal-ui-shared';
import { getYear } from 'date-fns';
import { useEffect, useState } from 'react';
import { AbsColor } from '../../constants';
import { ICalendarRiskGroup } from '../../contracts/calendar-risk-group.contract';
import { ICalendarRiskRegulation } from '../../contracts/calendar-risk-regulation.contract';
import { CalendarAction, CalendarFetch } from '../../features/calendar.feature';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { filterRegulationByOperationalOrHardware, filterRegulationsByOperationalOrHardware, Loading } from '../shared';
import CalendarRiskListItem from './calendar-risk-list-item.component';
import { ShowGuidelinesActions } from '../../features/showGuidelines.feature';

const CriteriaFilterStyle = {
    IconStyle: {
        color: AbsColor.White1
    },
    MenuStyle: {
        backgroundColor: AbsColor.Blue1,
        color: AbsColor.White1,
        borderTopLeftRadius: 10,
        borderTopRightRadius: 10,
        width: '100%'
    }
};

const filterRiskRegulations = (risks: ICalendarRiskRegulation[], showOperational: boolean): ICalendarRiskRegulation[] => {
    return risks
        .map((risk) => {
            const filteredRegulation = filterRegulationByOperationalOrHardware(risk.Regulation, showOperational);
            if (!filteredRegulation) return null;
            return { ...risk, Regulation: filteredRegulation };
        })
        .filter((risk): risk is ICalendarRiskRegulation => risk !== null);
};

const CalendarRiskList = () => {
    const dispatch = useAppDispatch();

    const selectedVessels = useAppSelector((state) => state.Calendar.Filters.Vessels);
    const riskRegulations = useAppSelector((state) => state.Calendar.Risk.List);
    const isLoading = useAppSelector((state) => state.Calendar.Risk.IsLoading);
    const showOperational = useAppSelector((state) => state.ShowGuidelines.showGuideLines);
    const surveyFilter = useAppSelector((state) => state.Calendar.Filters.Surveys);

    const [regulations, setRegulations] = useState<ICalendarRiskGroup[]>([]);
    const [selectedYear, setSelectedYear] = useState<string | undefined>(undefined);
    const [years, setYears] = useState<string[] | undefined>(undefined);

    useEffect(() => {
        if (!selectedVessels || selectedVessels.length === 0) {
            dispatch(CalendarAction.ClearRisks());
            return;
        }

        dispatch(CalendarFetch.GetRiskByClassNumberYear({ classNumbers: selectedVessels.flatMap((f) => f.ClassNumber), maxYear: getYear(new Date()) + 4 }));
    }, [selectedVessels, dispatch]);

    useEffect(() => {
        if ((!riskRegulations || riskRegulations.length === 0) && (!surveyFilter || surveyFilter.length === 0)) {
            setRegulations([]);
            return;
        }

        // implement survey(s) select
        if (surveyFilter && surveyFilter.length > 0) {
            const tempYears = Array.from(new Set(surveyFilter.map((s) => s.SurveyDueDate.split('-')[0]))).sort((a, b) => Number(a) - Number(b));
            setYears(tempYears);

            if (!selectedYear || !tempYears.includes(selectedYear)) {
                setSelectedYear(tempYears[0]);
            }

            const tempRegulations: ICalendarRiskGroup[] = tempYears.map((year) => {
                const yearRegulations = surveyFilter.filter((f) => f.SurveyDueDate.split('-')[0] === year).flatMap((f) => f.Regulations);
                const regulations = filterRegulationsByOperationalOrHardware(yearRegulations, showOperational);
                return {
                    Year: year.toString(),
                    HighRisks: regulations.map((regulation) => ({
                        Regulation: regulation,
                        Vessels: selectedVessels ?? []
                    })),
                    MediumRisks: [],
                    LowRisks: []
                };
            });

            setRegulations(tempRegulations);
            return;
        }

        // implement vessel(s) select
        if (riskRegulations && riskRegulations.length > 0) {
            const tempYears = riskRegulations.flatMap((f) => f.Year) ?? [];
            setYears(tempYears);

            if (!selectedYear) setSelectedYear(tempYears && tempYears.length > 0 ? tempYears[0] : undefined);

            const tempRegulations: ICalendarRiskGroup[] =
                riskRegulations.flatMap((risk) => {
                    return {
                        Year: risk.Year,
                        HighRisks: filterRiskRegulations(risk.HighRisks, showOperational),
                        MediumRisks: filterRiskRegulations(risk.MediumRisks, showOperational),
                        LowRisks: filterRiskRegulations(risk.LowRisks, showOperational)
                    };
                }) ?? [];

            setRegulations([...tempRegulations]);
        }
    }, [riskRegulations, showOperational, selectedYear, surveyFilter, selectedVessels]);

    const getHeaderTitle = (): string => {
        if (selectedVessels && selectedVessels.length > 0) {
            return `${selectedVessels.length} Vessel${selectedVessels.length > 1 ? 's' : ''} Selected`;
        }

        return `All Vessels Selected`;
    };

    return (
        <Stack direction={'column'} style={{ width: '100%' }} alignItems={'center'} justifyContent={'space-between'}>
            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1} style={{ width: '100%', height: '50px' }}>
                <Stack direction={'row'}>
                    <DirectionsBoatFilledIcon style={{ color: AbsColor.Blue1 }} />
                    <div>{getHeaderTitle()}</div>
                </Stack>
                <Stack direction={'row'} alignItems={'center'} gap={5}>
                    {years && (
                        <Stack direction={'row'} alignItems={'center'} gap={1}>
                            <Typography>Year:</Typography>
                            <AbsDropDown
                                selected={selectedYear}
                                handleChange={(e: { label: string; value: string }) => setSelectedYear(e.value)}
                                options={years.map((m) => {
                                    return {
                                        label: m,
                                        value: m
                                    };
                                })}
                            />
                        </Stack>
                    )}
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showOperational}
                                onChange={() => {
                                    dispatch(ShowGuidelinesActions.toggleShowGuidelines());
                                }}
                            />
                        }
                        label="Show Operational"
                    />
                </Stack>
            </Stack>
            <ListItemButton style={{ ...CriteriaFilterStyle.MenuStyle }}>
                <ListItemIcon>
                    <CheckIcon style={{ ...CriteriaFilterStyle.IconStyle }} />
                </ListItemIcon>
                <ListItemText primary={`Showing Hardware` + `${showOperational ? ' and Operational' : ''} in Year ${selectedYear}`} />
            </ListItemButton>

            {isLoading ? (
                <Stack style={{ padding: 5 }}>
                    <Loading />
                </Stack>
            ) : (
                <>
                    {regulations
                        .filter((f) => f.Year === selectedYear)
                        .map((risks) => (
                            <CalendarRiskListItem risks={risks} />
                        ))}
                </>
            )}
        </Stack>
    );
};

export default CalendarRiskList;
